import React, { useRef, useState } from 'react';
import { useReactive } from 'ahooks';
import { Input, Button, Upload } from 'kenshin';
import {
  SendOutlined,
  PaperClipOutlined,
  PlusOutlined,
  CheckOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import styles from './index.less';

const MODES = {
  CHAT: 'chat',
  IMAGE: 'image',
  VIDEO: 'video',
};

// AI会话模型选项
const chatModelOptions = [
  { label: 'OpenAI GPT', value: 'openai_gpt' },
  { label: '阿里通义', value: 'ali_tongyi', children: [
    { label: '万相2.6', value: 'wanxiang_2.6' },
    { label: '万相2.7', value: 'wanxiang_2.7' },
    { label: '通义千问', value: 'tongyi_qianwen' },
  ]},
  { label: '腾讯混元', value: 'tencent_hunyuan' },
  { label: '字节豆包', value: 'bytedance_doubao' },
  { label: '豆包', value: 'doubao' },
];

// AI生图模型选项
const imageModelOptions = [
  { label: '图片5.0 Lite', value: 'image_5.0_lite', desc: '指令响应更精准，生成效果更智能' },
  { label: '图片4.7', value: 'image_4.7', desc: '画面全面优化，指令响应能力再次提升' },
  { label: '图片4.6', value: 'image_4.6', desc: '人像一致性保持良好，性价比更高' },
  { label: '图片4.5', value: 'image_4.5', desc: '强化一致性，风格与图文描述' },
  { label: '图片4.1', value: 'image_4.1', desc: '提供基础的图像，整体和一致性保持' },
];

// AI生图比例选项
const imageRatioOptions = [
  { label: '9:16', value: '9:16' },
  { label: '2:3', value: '2:3' },
  { label: '3:4', value: '3:4' },
  { label: '1:1', value: '1:1' },
  { label: '4:3', value: '4:3' },
  { label: '3:2', value: '3:2' },
  { label: '16:9', value: '16:9' },
];

// AI视频模型选项
const videoModelOptions = [
  { label: 'HappyHorse 1.0 Beta', value: 'happyhorse_1.0', desc: '极致细节，真实自然的表达' },
  { label: '万相2.7', value: 'wanxiang_2.7', desc: '全能参考，让角色演绎起来' },
  { label: '万相2.6', value: 'wanxiang_2.6', desc: '多镜头叙事，动态时长生成' },
];

// AI视频比例选项
const videoRatioOptions = [
  { label: '21:9', value: '21:9' },
  { label: '16:9', value: '16:9' },
  { label: '4:3', value: '4:3' },
  { label: '1:1', value: '1:1' },
  { label: '3:4', value: '3:4' },
  { label: '9:16', value: '9:16' },
];

// AI视频时长选项
const videoDurationOptions = [
  { label: '9s', value: '9' },
  { label: '10s', value: '10' },
  { label: '11s', value: '11' },
  { label: '12s', value: '12' },
  { label: '13s', value: '13' },
  { label: '14s', value: '14' },
  { label: '15s', value: '15' },
];

// 数量选项
const quantityOptions = [
  { label: '1张', value: 1 },
  { label: '2张', value: 2 },
  { label: '3张', value: 3 },
  { label: '4张', value: 4 },
];

const InputArea = (props) => {
  const { mode, onSend } = props;
  const textareaRef = useRef();

  const state = useReactive({
    text: '',
    images: [],
    // 生图参数
    imageModel: 'image_4.1',
    imageRatio: '3:2',
    imageQuality: '高清2k',
    imageQuantity: 4,
    // 视频参数
    videoModel: 'wanxiang_2.7',
    videoRatio: '16:9',
    videoQuality: '1080P',
    videoDuration: '10',
    // 会话参数
    chatModel: 'wanxiang_2.6',
    // 下拉展开状态
    showImageModel: false,
    showImageRatio: false,
    showVideoModel: false,
    showVideoRatio: false,
    showVideoDuration: false,
    showChatModel: false,
  });

  // 发送消息
  const handleSend = () => {
    if (!state.text.trim() && state.images.length === 0) return;
    onSend({
      text: state.text,
      images: [...state.images],
      mode,
      params: mode === MODES.IMAGE ? {
        model: state.imageModel,
        ratio: state.imageRatio,
        quality: state.imageQuality,
        quantity: state.imageQuantity,
      } : mode === MODES.VIDEO ? {
        model: state.videoModel,
        ratio: state.videoRatio,
        quality: state.videoQuality,
        duration: state.videoDuration,
      } : {
        model: state.chatModel,
      },
    });
    state.text = '';
    state.images = [];
  };

  // 上传图片
  const handleUpload = (file) => {
    const url = URL.createObjectURL(file);
    state.images = [...state.images, url];
    return false; // 阻止默认上传行为
  };

  // 移除图片
  const removeImage = (index) => {
    state.images = state.images.filter((_, i) => i !== index);
  };

  // 键盘事件
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // 渲染下拉选项
  const renderDropdown = (options, value, onChange, showKey, closeKey) => (
    <div className={styles.dropdownWrapper}>
      <div
        className={styles.dropdownTrigger}
        onClick={() => (state[showKey] = !state[showKey])}
      >
        {options.find((o) => o.value === value)?.label || options[0]?.label}
      </div>
      {state[showKey] && (
        <div className={styles.dropdownMenu}>
          {options.map((opt) => (
            <div
              key={opt.value}
              className={`${styles.dropdownItem} ${opt.value === value ? styles.dropdownItemActive : ''}`}
              onClick={() => {
                onChange(opt.value);
                state[showKey] = false;
              }}
            >
              <span className={styles.dropdownItemLabel}>{opt.label}</span>
              {opt.desc && <span className={styles.dropdownItemDesc}>{opt.desc}</span>}
              {opt.value === value && <CheckOutlined className={styles.dropdownItemCheck} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // 渲染AI会话输入区
  const renderChatInput = () => (
    <div className={styles.inputWrapper}>
      <div className={styles.paramRow}>
        {renderDropdown(
          chatModelOptions,
          state.chatModel,
          (v) => (state.chatModel = v),
          'showChatModel',
          'showChatModel'
        )}
      </div>
    </div>
  );

  // 渲染AI生图输入区
  const renderImageInput = () => (
    <div className={styles.inputWrapper}>
      <div className={styles.paramRow}>
        {renderDropdown(
          imageModelOptions,
          state.imageModel,
          (v) => (state.imageModel = v),
          'showImageModel',
          'showImageModel'
        )}
        {renderDropdown(
          imageRatioOptions,
          state.imageRatio,
          (v) => (state.imageRatio = v),
          'showImageRatio',
          'showImageRatio'
        )}
        <div className={styles.dropdownWrapper}>
          <div className={styles.dropdownTrigger}>高清2k</div>
        </div>
        {renderDropdown(
          quantityOptions,
          state.imageQuantity,
          (v) => (state.imageQuantity = v),
          'showImageQuantity',
          'showImageQuantity'
        )}
      </div>
    </div>
  );

  // 渲染AI视频输入区
  const renderVideoInput = () => (
    <div className={styles.inputWrapper}>
      <div className={styles.paramRow}>
        {renderDropdown(
          videoModelOptions,
          state.videoModel,
          (v) => (state.videoModel = v),
          'showVideoModel',
          'showVideoModel'
        )}
        {renderDropdown(
          videoRatioOptions,
          state.videoRatio,
          (v) => (state.videoRatio = v),
          'showVideoRatio',
          'showVideoRatio'
        )}
        <div className={styles.dropdownWrapper}>
          <div className={styles.dropdownTrigger}>1080P</div>
        </div>
        {renderDropdown(
          videoDurationOptions,
          state.videoDuration,
          (v) => (state.videoDuration = v),
          'showVideoDuration',
          'showVideoDuration'
        )}
      </div>
    </div>
  );

  const modeLabels = {
    [MODES.CHAT]: 'AI会话',
    [MODES.IMAGE]: 'AI生图',
    [MODES.VIDEO]: 'AI视频',
  };

  const placeholderTexts = {
    [MODES.CHAT]: '请问有什么需要帮助的？',
    [MODES.IMAGE]: '上传参考图，输入文字，描述你想要生成的图片。',
    [MODES.VIDEO]: '上传参考图，输入文字，描述你想要生成的视频。',
  };

  return (
    <div className={styles.inputArea}>
      {/* 已上传的图片预览 */}
      {state.images.length > 0 && (
        <div className={styles.uploadedImages}>
          {state.images.map((img, idx) => (
            <div key={idx} className={styles.uploadedImgItem}>
              <img src={img} alt="" />
              <div className={styles.uploadedImgRemove} onClick={() => removeImage(idx)}>
                <CloseOutlined />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 文本输入 */}
      <Input.TextArea
        ref={textareaRef}
        value={state.text}
        onChange={(e) => (state.text = e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholderTexts[mode]}
        className={styles.textarea}
        autoSize={{ minRows: 2, maxRows: 6 }}
        bordered={false}
      />

      {/* 参数选择行 */}
      {mode === MODES.CHAT && renderChatInput()}
      {mode === MODES.IMAGE && renderImageInput()}
      {mode === MODES.VIDEO && renderVideoInput()}

      {/* 底部操作栏 */}
      <div className={styles.inputFooter}>
        <div className={styles.footerLeft}>
          <Upload
            accept="image/*"
            showUploadList={false}
            beforeUpload={handleUpload}
          >
            <PaperClipOutlined className={styles.footerIcon} />
          </Upload>
        </div>
        <div className={styles.footerRight}>
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleSend}
            disabled={!state.text.trim() && state.images.length === 0}
            className={styles.sendBtn}
          />
        </div>
      </div>
    </div>
  );
};

export default InputArea;
